/* Native behavior for the Liquid start-page sections. No external runtime required. */
(() => {
  'use strict';
  if (window.reviveStartPageBound) return;
  window.reviveStartPageBound = true;

  const players = new WeakMap();

  let offerRail = null;
  let mobilePurchaseBar = null;
  let offerRailFrame = 0;

  function isVisibleInViewport(selector) {
    return Array.from(document.querySelectorAll(selector)).some((element) => {
      if (element.hidden || element.closest('[hidden]') || getComputedStyle(element).visibility === 'hidden') return false;
      const bounds = element.getBoundingClientRect();
      return bounds.height > 0 && bounds.top < window.innerHeight && bounds.bottom > 0;
    });
  }

  function isNearViewport(selector, offset = 0) {
    return Array.from(document.querySelectorAll(selector)).some((element) => {
      if (element.hidden || element.closest('[hidden]') || getComputedStyle(element).visibility === 'hidden') return false;
      const bounds = element.getBoundingClientRect();
      return bounds.height > 0
        && bounds.top < window.innerHeight + offset
        && bounds.bottom > -offset;
    });
  }

  function hasPassedViewport(selector) {
    const element = document.querySelector(selector);
    if (!element) return window.scrollY > 120;
    return element.getBoundingClientRect().bottom <= 0;
  }

  function updateOfferRail() {
    offerRailFrame = 0;
    if (!offerRail?.isConnected) offerRail = document.querySelector('[data-start-offer-rail]');
    if (!mobilePurchaseBar?.isConnected) {
      mobilePurchaseBar = document.querySelector('[data-start-mobile-purchase-bar]');
    }

    const modalOpen = Boolean(document.querySelector('[data-start-signup][open]'));
    if (offerRail) {
      const shouldShowRail = window.innerWidth >= 1200
        && !modalOpen
        && hasPassedViewport('#hero-cta')
        && !isVisibleInViewport('#choose-your-set')
        && !isVisibleInViewport('#site-footer');

      offerRail.setAttribute('aria-hidden', String(!shouldShowRail));
      offerRail.style.opacity = shouldShowRail ? '1' : '0';
      offerRail.style.visibility = shouldShowRail ? 'visible' : 'hidden';
      offerRail.style.pointerEvents = shouldShowRail ? 'auto' : 'none';
      const button = offerRail.querySelector('.rail-cta');
      if (button) button.tabIndex = shouldShowRail ? 0 : -1;
    }

    if (mobilePurchaseBar) {
      const boundaryOffset = mobilePurchaseBar.offsetHeight || 64;
      const shouldShowMobileBar = window.innerWidth < 768
        && !modalOpen
        && hasPassedViewport('#hero-cta')
        && !isVisibleInViewport('.bridge-cta, .close-cta, .final-cta')
        && !isNearViewport('#choose-your-set', boundaryOffset)
        && !isNearViewport('#site-footer', boundaryOffset);

      mobilePurchaseBar.setAttribute('aria-hidden', String(!shouldShowMobileBar));
      mobilePurchaseBar.style.transform = shouldShowMobileBar ? 'translateY(0)' : 'translateY(110%)';
      mobilePurchaseBar.style.opacity = shouldShowMobileBar ? '1' : '0';
      mobilePurchaseBar.style.visibility = shouldShowMobileBar ? 'visible' : 'hidden';
      mobilePurchaseBar.style.pointerEvents = shouldShowMobileBar ? 'auto' : 'none';
      const button = mobilePurchaseBar.querySelector('.sb-btn');
      if (button) button.tabIndex = shouldShowMobileBar ? 0 : -1;
    }
  }

  function scheduleOfferRailUpdate() {
    if (offerRailFrame) return;
    offerRailFrame = window.requestAnimationFrame(updateOfferRail);
  }

  function bindOfferRail(rail) {
    offerRail = rail;
    scheduleOfferRailUpdate();
  }

  function bindMobilePurchaseBar(bar) {
    mobilePurchaseBar = bar;
    scheduleOfferRailUpdate();
  }

  function bindPlayer(video) {
    if (players.has(video)) return players.get(video);
    const cell = video.closest('.video-cell');
    const button = cell?.querySelector('.video-play');
    const overlay = cell?.querySelector('[data-video-overlay]');
    if (!button || !overlay) return null;
    const label = button.querySelector('.video-play-label');
    const originalLabel = label?.textContent;
    const originalAria = button.getAttribute('aria-label');
    const state = { pending: false };

    const showOverlay = () => {
      overlay.hidden = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      state.pending = false;
    };
    const resetLabel = () => {
      if (label) label.textContent = originalLabel;
      if (originalAria) button.setAttribute('aria-label', originalAria);
    };
    const fail = () => {
      showOverlay();
      const message = button.dataset.errorLabel || 'Video unavailable. Tap to retry.';
      if (label) label.textContent = message;
      button.setAttribute('aria-label', message);
      video.controls = true;
    };
    video.addEventListener('playing', () => {
      resetLabel();
      overlay.hidden = true;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      state.pending = false;
      document.querySelectorAll('.video-cell video').forEach((other) => {
        if (other !== video && !other.paused) other.pause();
      });
    });
    // Keep native controls accessible while paused; restore the poster button on end.
    video.addEventListener('ended', () => {
      resetLabel();
      showOverlay();
      video.controls = false;
    });
    video.addEventListener('error', fail);
    state.play = () => {
      if (state.pending) return;
      resetLabel();
      const hlsSource = video.dataset.hlsSrc?.trim();
      const source = hlsSource && video.canPlayType('application/vnd.apple.mpegurl')
        ? hlsSource
        : video.dataset.src?.trim();
      if (!source) { fail(); return; }
      try {
        const url = new URL(source, window.location.href);
        if (!['https:', 'http:'].includes(url.protocol)) { fail(); return; }
        const retry = Boolean(video.error);
        if (!video.getAttribute('src')) video.setAttribute('src', url.href);
        else if (retry) video.load();
        if (video.ended) video.currentTime = 0;
        state.pending = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        video.controls = true;
        // Call synchronously inside the click gesture for mobile browser support.
        const playback = video.play();
        if (playback?.catch) playback.catch(fail);
      } catch (error) {
        fail();
      }
    };
    players.set(video, state);
    return state;
  }

  let selectedStartVariant = null;
  let selectedStartSet = 'both';

  function renderFinalLines() {
    document.querySelectorAll('[data-final-line]').forEach(element => {
      element.textContent = element.dataset[selectedStartSet] || element.dataset.both || '';
    });
  }

  function renderReadyCard(section, item) {
    if (!section || !item) return;
    const title = section.querySelector('[data-ready-variant-title]');
    const price = section.querySelector('[data-ready-price]');
    if (title) title.textContent = item.title || '';
    if (price) price.textContent = item.money || '';
    section.querySelectorAll('[data-start-offer-price]').forEach(element => {
      element.textContent = item.offerMoney || '';
    });
  }

  function publishVariant(item) {
    if (!item) return;
    selectedStartVariant = item;
    document.querySelectorAll('[data-ready-card]').forEach(section => renderReadyCard(section, item));
  }

  const readyCards = new WeakSet();

  function bindReadyCard(section) {
    if (readyCards.has(section)) return;
    readyCards.add(section);
    renderReadyCard(section, selectedStartVariant);
  }

  const buyBoxes = new WeakSet();

  function bindChooseSet(section) {
    if (buyBoxes.has(section)) return;
    const configElement = section.querySelector('[data-set-config]');
    if (!configElement) return;
    let config;
    try { config = JSON.parse(configElement.textContent); } catch { return; }
    config.orderError ||= 'We could not open checkout. Please try again.';
    config.loadingLabel ||= 'Preparing checkout…';
    buyBoxes.add(section);
    const options = Array.from(section.querySelectorAll('[data-variant-id]'));
    const order = section.querySelector('[data-set-order]');
    const orderLabel = order.querySelector('[data-order-label]');
    const idleLabel = orderLabel.textContent;
    const terms = section.querySelector('input[type="checkbox"]');
    const termsError = section.querySelector('.terms-err');
    const orderError = section.querySelector('[data-order-error]');
    const root = section.dataset.cartRoot || '/';
    const savedDiscount = getStoredDiscount();
    const savedOption = savedDiscount && options.find(option => !option.disabled && (savedDiscount.variantId ? option.dataset.variantId === savedDiscount.variantId : option.dataset.set === savedDiscount.set));
    let chosen = savedOption || options.find(option => option.getAttribute('aria-checked') === 'true' && !option.disabled);
    let busy = false;
    let addedVariant = null;

    const variant = () => config.variants.find(item => String(item.id) === chosen?.dataset.variantId);
    function refreshPrice() {
      const item = variant();
      const price = section.querySelector('[data-set-price]');
      const strike = section.querySelector('.bb-strike');
      if (strike) strike.textContent = item?.money || '';
      selectedStartSet = chosen?.dataset.set || 'both';
      renderFinalLines();

      const originalPrice = parseFloat(
        (item?.money || '').replace(/[^0-9.-]+/g, '')
      );

      price.textContent = Number.isFinite(originalPrice)
        ? `$${(originalPrice - 300).toLocaleString()}`
        : '';

      section.querySelectorAll('[data-start-offer-price]').forEach(element => {
        element.textContent = item?.offerMoney || '';
      });
      const affirmWidget = section.querySelector('[data-start-affirm]');
      if (affirmWidget && item?.price != null) {
        const amount = String(item.price);
        if (affirmWidget.getAttribute('data-amount') !== amount) {
          affirmWidget.setAttribute('data-amount', amount);
        }
        const refreshAffirm = () => {
          if (window.affirm?.ui?.refresh) window.affirm.ui.refresh();
        };
        if (window.affirm?.ui?.ready) window.affirm.ui.ready(refreshAffirm);
        else refreshAffirm();
      }
      publishVariant(item);
    }
    function select(option, focus = false) {
      if (!option || option.disabled || busy || addedVariant) return;
      chosen = option;
      options.forEach(item => {
        item.setAttribute('aria-checked', String(item === option));
        item.tabIndex = item === option ? 0 : -1;
      });
      refreshPrice();
      order.disabled = !variant()?.available;
      if (focus) option.focus();
    }
    options.forEach(option => option.addEventListener('click', () => select(option)));
    section.querySelector('[role="radiogroup"]').addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const enabled = options.filter(option => !option.disabled);
      const index = enabled.indexOf(chosen);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + enabled.length) % enabled.length;
      select(enabled[next], true);
    });
    select(chosen || options.find(option => !option.disabled));

    const slides = Array.from(section.querySelectorAll('[data-gallery-slide]'));
    const thumbs = Array.from(section.querySelectorAll('[data-gallery-index]'));
    const stage = section.querySelector('[data-set-gallery]');
    let slide = 0;
    let touchX = null;
    function showSlide(index) {
      if (!slides.length) return;
      slide = (index + slides.length) % slides.length;
      slides.forEach((image, i) => { image.hidden = i !== slide; });
      thumbs.forEach((button, i) => button.setAttribute('aria-current', String(i === slide)));
      section.querySelector('[data-gallery-counter]').textContent = `${slide + 1} / ${slides.length}`;
      const caption = section.querySelector('[data-gallery-caption]');
      if (caption) caption.hidden = caption.dataset.imageId !== slides[slide].dataset.imageId;
    }
    showSlide(0);
    thumbs.forEach((button, index) => button.addEventListener('click', () => showSlide(index)));
    section.querySelector('[data-gallery-prev]')?.addEventListener('click', () => showSlide(slide - 1));
    section.querySelector('[data-gallery-next]')?.addEventListener('click', () => showSlide(slide + 1));
    stage.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      showSlide(slide + (event.key === 'ArrowLeft' ? -1 : 1));
    });
    stage.addEventListener('touchstart', event => { touchX = event.touches[0]?.clientX ?? null; }, { passive: true });
    stage.addEventListener('touchend', event => {
      if (touchX === null) return;
      const delta = (event.changedTouches[0]?.clientX ?? touchX) - touchX;
      if (Math.abs(delta) > 40) showSlide(slide + (delta < 0 ? 1 : -1));
      touchX = null;
    }, { passive: true });

    async function post(url, body) {
      const endpoint = new URL(url, window.location.href);
      if (!['https:', 'http:'].includes(endpoint.protocol)) throw new Error('Invalid endpoint');
      const response = await fetch(endpoint.href, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body), signal: AbortSignal.timeout(20000),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Request failed');
      return data;
    }
    terms.addEventListener('change', () => {
      if (!terms.checked) return;
      terms.setAttribute('aria-invalid', 'false');
      section.querySelector('.terms').classList.remove('err');
      termsError.hidden = true;
    });
    order.addEventListener('click', async () => {
      if (busy || !variant()?.available) return;
      if (!terms.checked) {
        terms.setAttribute('aria-invalid', 'true');
        section.querySelector('.terms').classList.add('err');
        termsError.hidden = false;
        terms.focus();
        return;
      }
      busy = true;
      order.disabled = true;
      order.setAttribute('aria-busy', 'true');
      orderLabel.textContent = config.loadingLabel;
      orderError.hidden = true;
      try {
        const id = String(variant().id);
        if (!addedVariant) {
          await post(`${root}cart/clear.js`, {});
          await post(`${root}cart/add.js`, {
            items: [{
              id,
              quantity: 1,
              properties: {
                Includes: 'At-home impression kit',
                Remakes: 'Up to two free, within 30 days of delivery',
              },
            }]
          });
          // Retain the added selection on a cart-update failure so retries do not add it twice.
          addedVariant = id;
          options.forEach(option => { option.disabled = true; });
        }
        await post(`${root}cart/update.js`, {
          attributes: {
            terms_accepted: 'Yes', terms_accepted_at: new Date().toISOString(), accepted_from: 'Start Page',
          }
        });
        window.location.assign(new URL(`${root}checkout`, window.location.origin).href);
      } catch {
        orderError.textContent = config.orderError;
        orderError.hidden = false;
      } finally {
        busy = false;
        order.disabled = !variant()?.available;
        order.setAttribute('aria-busy', 'false');
        orderLabel.textContent = idleLabel;
      }
    });
  }

  const signupDialogs = new WeakSet();

  function clearStoredDiscount() {
    try {
      ['revive_code', 'revive_code_expires_at', 'revive_selected_set'].forEach(key => sessionStorage.removeItem(key));
    } catch { /* Storage can be disabled; the UI must still remain usable. */ }
  }

  function getStoredDiscount() {
    try {
      const rawCode = sessionStorage.getItem('revive_code') || '';
      let stored;
      try { stored = JSON.parse(rawCode); }
      catch { stored = { code: rawCode, expiresAt: sessionStorage.getItem('revive_code_expires_at') }; }
      if (!stored || typeof stored.code !== 'string' || !/^REVIVE-[A-Z0-9]{8}$/.test(stored.code)
        || typeof stored.expiresAt !== 'string' || !Number.isFinite(Date.parse(stored.expiresAt))
        || Date.parse(stored.expiresAt) <= Date.now()) {
        clearStoredDiscount();
        return null;
      }
      return stored;
    } catch {
      clearStoredDiscount();
      return null;
    }
  }

  let discountExpiryTimer = 0;
  function restoreDiscountState(shouldScroll = false) {
    const discount = getStoredDiscount();
    document.querySelectorAll('[data-start-signup-initial]').forEach(element => { element.hidden = Boolean(discount); });
    document.querySelectorAll('[data-start-signup-reopen]').forEach(element => { element.hidden = true; });
    document.querySelectorAll('[data-start-offer-applied]').forEach(element => { element.hidden = !discount; });
    clearTimeout(discountExpiryTimer);
    if (!discount) return;
    discountExpiryTimer = window.setTimeout(() => restoreDiscountState(), Math.min(2147483647, Math.max(1, Date.parse(discount.expiresAt) - Date.now())));
    if (shouldScroll) {
      requestAnimationFrame(() => document.querySelector('#choose-your-set')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }

  function bindSignup(dialog) {
    if (signupDialogs.has(dialog)) return;
    signupDialogs.add(dialog);
    const section = dialog.closest('[data-start-offer-section]');
    const triggers = Array.from(section?.querySelectorAll('[data-start-signup-open]') || []);
    const signupForm = dialog.querySelector('[data-start-klaviyo-form]');
    const idleSubmitLabel = signupForm?.querySelector('[data-signup-submit]')?.textContent || 'Get $300 off';
    let previousOverflow = '';
    let opener = null;
    let submitting = false;
    function open() {
      if (dialog.open) return;
      opener = document.activeElement;
      if (!submitting && !getStoredDiscount()) {
        const submit = signupForm?.querySelector('[data-signup-submit]');
        if (submit) { submit.disabled = false; submit.textContent = idleSubmitLabel; }
        const feedback = signupForm?.querySelector('[data-signup-feedback]');
        if (feedback) feedback.hidden = true;
      }
      previousOverflow = document.documentElement.style.overflow;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'true'));
      (dialog.querySelector('[data-signup-success]') || dialog.querySelector('input[type="email"]'))?.focus();
      scheduleOfferRailUpdate();
    }
    triggers.forEach(trigger => trigger.addEventListener('click', open));
    signupForm?.addEventListener('submit', async event => {
      event.preventDefault();
      if (submitting || !signupForm.reportValidity()) return;
      const email = signupForm.querySelector('input[type="email"]');
      const submit = signupForm.querySelector('[data-signup-submit]');
      const feedback = signupForm.querySelector('[data-signup-feedback]');
      const listId = signupForm.dataset.listId?.trim();
      const endpoint = signupForm.dataset.discountEndpoint?.trim();
      const originalLabel = submit?.textContent || '';
      if (!email || !submit || !feedback) return;
      const submittedEmail = email.value.trim();
      const selectedOption = document.querySelector('[data-choose-set] [data-variant-id][aria-checked="true"]');
      const selectedSet = selectedOption?.dataset.set || 'both';
      const selectedVariantId = selectedOption?.dataset.variantId || '';
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 10000);
      submitting = true;
      let succeeded = false;
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = 'Sending…';
      feedback.hidden = true;
      try {
        if (!listId) throw new Error('Missing Klaviyo list ID');
        if (!endpoint) throw new Error('Missing discount service endpoint');
        const response = await fetch(endpoint, {
          signal: controller.signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: submittedEmail, listId, discountAmount: 300, expiresInDays: 7, codePrefix: 'REVIVE-' }),
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || result?.success !== true || typeof result.code !== 'string'
          || !/^REVIVE-[A-Z0-9]{8}$/.test(result.code) || typeof result.expiresAt !== 'string'
          || !Number.isFinite(Date.parse(result.expiresAt)) || Date.parse(result.expiresAt) <= Date.now()) {
          throw new Error('Invalid discount response');
        }
        const discount = { code: result.code, expiresAt: result.expiresAt, set: selectedSet, variantId: selectedVariantId };
        sessionStorage.setItem('revive_code', JSON.stringify(discount));
        if (getStoredDiscount()?.code !== discount.code) throw new Error('Discount was not saved');
        // Optional analytics must never delay the required success/redirect flow.
        // Release gate: publish this email-delivery copy only after Part A passes.
        try { Promise.resolve(window.klaviyo?.identify?.({ email: submittedEmail })).catch(() => {}); } catch { /* Worker is authoritative. */ }
        feedback.textContent = `Your $300 code is on its way to ${submittedEmail}.`;
        feedback.style.color = 'var(--green, #047857)';
        feedback.hidden = false;
        email.value = '';
        submit.textContent = 'Ready';
        succeeded = true;
        window.setTimeout(() => {
          if (!getStoredDiscount()) {
            feedback.textContent = 'Something went wrong. Please try again.';
            feedback.style.color = 'var(--danger)';
            submit.disabled = false;
            submit.textContent = originalLabel;
            restoreDiscountState();
            return;
          }
          dialog.close();
          window.location.assign(`/discount/${encodeURIComponent(discount.code)}?redirect=/pages/start`);
        }, 1000);
      } catch {
        feedback.textContent = 'Something went wrong. Please try again.';
        feedback.style.color = 'var(--danger)';
        feedback.hidden = false;
        submit.textContent = originalLabel;
      } finally {
        clearTimeout(timeout);
        submitting = false;
        submit.disabled = succeeded;
        submit.setAttribute('aria-busy', 'false');
      }
    });
    dialog.querySelectorAll('[data-start-signup-close]').forEach(button => {
      button.addEventListener('click', () => dialog.close());
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      if (dialog.querySelector('[data-start-signup-panel]')) {
        dialog.close();
        return;
      }
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = previousOverflow;
      triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      if (opener?.isConnected) opener.focus();
      scheduleOfferRailUpdate();
    });
  }

  function initialize(root) {
    root.querySelectorAll('[data-start-offer-rail]').forEach(bindOfferRail);
    root.querySelectorAll('[data-start-mobile-purchase-bar]').forEach(bindMobilePurchaseBar);
    root.querySelectorAll('.video-cell video').forEach(bindPlayer);
    root.querySelectorAll('[data-choose-set]').forEach(bindChooseSet);
    root.querySelectorAll('[data-ready-card]').forEach(bindReadyCard);
    root.querySelectorAll('[data-start-signup]').forEach(bindSignup);
    renderFinalLines();
    restoreDiscountState(root === document);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const playButton = event.target.closest('.video-play');
    if (playButton) {
      const video = playButton.closest('.video-cell')?.querySelector('video');
      if (video) { event.preventDefault(); bindPlayer(video)?.play(); }
      return;
    }
    const question = event.target.closest('.faq-q');
    if (question) {
      const panel = document.getElementById(question.getAttribute('aria-controls'));
      if (!panel) return;
      const expanded = question.getAttribute('aria-expanded') === 'true';
      question.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
      const symbol = question.querySelector('[aria-hidden="true"]');
      if (symbol) symbol.textContent = expanded ? '+' : '−';
      return;
    }
    const link = event.target.closest('[data-start-link]');
    if (link?.dataset.startLink) {
      const url = new URL(link.dataset.startLink, window.location.href);
      if (['https:', 'http:'].includes(url.protocol)) window.location.assign(url.href);
    }
  });

  window.addEventListener('pageshow', () => restoreDiscountState());
  document.addEventListener('visibilitychange', () => { if (!document.hidden) restoreDiscountState(); });
  window.addEventListener('scroll', scheduleOfferRailUpdate, { passive: true });
  window.addEventListener('resize', scheduleOfferRailUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (mobilePurchaseBar && event.target.contains(mobilePurchaseBar)) mobilePurchaseBar = null;
    event.target.querySelectorAll('video').forEach((video) => video.pause());
    event.target.querySelectorAll('[data-start-signup]').forEach((dialog) => { if (dialog.open) dialog.close(); });
  });
})();
